import React, { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, FileText } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermission';
import {
  usePerformanceServices,
  useCreatePerformanceService,
  useUpdatePerformanceService,
  useDeletePerformanceService,
  useExternalOverrides,
  useUpsertOverride,
  useDeleteOverride,
} from '../../api/usePerformances';
import type { PerformanceService, ExternalPhotographer } from '../../api/types';
import { apiGet } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Modal, ConfirmDialog } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonRow } from '../../components/ui/Skeleton';
import { ServiceExportPdfDrawer } from '../../components/performances/ServiceExportPdfDrawer';
import { formatCurrency } from '../../utils/format';

const TOGGLE_STYLE = {
  group: { display: 'inline-flex' as const, gap: 0, borderRadius: 'var(--radius-card)', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.10)' },
  btn: (active: boolean): React.CSSProperties => ({
    padding: '0.375rem 0.875rem', fontSize: 'var(--text-sm)', fontWeight: active ? 600 : 400,
    border: 'none', cursor: 'pointer', outline: 'none',
    background: active ? 'var(--color-primary)' : 'var(--glass-bg-card)',
    color: active ? '#fff' : 'var(--color-text)',
  }),
};

const CATALOG_SCOPES = ['all', 'global', 'personal'] as const;

export function ServiceCatalogTab() {
  const { hasPermission } = usePermissions();
  const canCatalogEdit = hasPermission('performances.catalog.edit');
  const [catalogScope, setCatalogScope] = useState<'all' | 'global' | 'personal'>('all');

  const { data: services, isLoading } = usePerformanceServices(catalogScope);
  const createMutation = useCreatePerformanceService();
  const updateMutation = useUpdatePerformanceService();
  const deleteMutation = useDeletePerformanceService();

  const [showForm, setShowForm] = useState(false);
  const [editService, setEditService] = useState<PerformanceService | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PerformanceService | null>(null);
  const [name, setName] = useState('');
  const [description, setDesc] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [cumulable, setCumulable] = useState(true);
  const [conflictGroup, setConflictGroup] = useState('');
  const [selectedExtId, setSelectedExtId] = useState('');
  const [showExportDrawer, setShowExportDrawer] = useState(false);

  const { data: externalsRaw } = useQuery<ExternalPhotographer[] | { data: ExternalPhotographer[] }>({
    queryKey: ['external-photographers', 'catalog-picker'],
    queryFn: () => apiGet('/external-photographers?limit=100&active=true'),
  });
  const externals: ExternalPhotographer[] = Array.isArray(externalsRaw)
    ? externalsRaw
    : (externalsRaw as { data: ExternalPhotographer[] })?.data ?? [];

  const selectedExtName = useMemo(() => {
    if (!selectedExtId) return undefined;
    const ext = externals.find((e: ExternalPhotographer) => e.id === selectedExtId);
    return ext ? (ext.studioName ?? `${ext.firstName} ${ext.lastName}`) : undefined;
  }, [selectedExtId, externals]);

  const { data: overrideEntries = [] } = useExternalOverrides(selectedExtId || null);
  const overrideMap = useMemo(
    () => new Map(overrideEntries.map(e => [e.service.id, e])),
    [overrideEntries]
  );

  const upsertOverride = useUpsertOverride();
  const deleteOverride = useDeleteOverride();
  const [editingOverrideServiceId, setEditingOverrideServiceId] = useState<string | null>(null);
  const [editingOverrideValue, setEditingOverrideValue] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  const showExtCol = !!selectedExtId;
  const gridCols = showExtCol
    ? '2fr 1fr 0.7fr 0.9fr 0.5fr 44px'
    : '2fr 1fr 0.5fr 0.5fr 44px';

  const openCreate = () => {
    setEditService(null);
    setName('');
    setDesc('');
    setBasePrice('');
    setCumulable(true);
    setConflictGroup('');
    setShowForm(true);
  };

  const openEdit = (s: PerformanceService) => {
    setEditService(s);
    setName(s.name);
    setDesc(s.description ?? '');
    setBasePrice(String(s.basePrice));
    setCumulable(s.cumulable);
    setConflictGroup(s.conflictGroup ?? '');
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !basePrice) { toast.error('Nome e prezzo obbligatori'); return; }

    const body = {
      name,
      description: description || undefined,
      basePrice: Number(basePrice),
      cumulable: true,
      conflictGroup: conflictGroup || undefined,
      scope: (catalogScope === 'global' ? 'global' : 'personal') as 'global' | 'personal',
    };

    if (editService) {
      updateMutation.mutate({ id: editService.id, ...body }, {
        onSuccess: () => { toast.success('Servizio aggiornato'); setShowForm(false); },
        onError: (e: Error) => toast.error(e.message),
      });
    } else {
      createMutation.mutate(body, {
        onSuccess: () => { toast.success('Servizio creato'); setShowForm(false); },
        onError: (e: Error) => toast.error(e.message),
      });
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => { toast.success('Servizio rimosso'); setDeleteTarget(null); },
      onError: (e: Error) => toast.error(e.message),
    });
  };

  const activeServices = (services ?? []).filter(s => s.isActive);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <select
          value={selectedExtId}
          onChange={e => setSelectedExtId(e.target.value)}
          style={{
            background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)', color: selectedExtId ? 'var(--color-text)' : 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', outline: 'none', minWidth: 200,
          }}
        >
          <option value="">Mostra prezzi per esterno…</option>
          {externals.map((ext: ExternalPhotographer) => (
            <option key={ext.id} value={ext.id}>
              {ext.studioName ?? `${ext.firstName} ${ext.lastName}`}
            </option>
          ))}
        </select>
        <Button onClick={openCreate} icon={<Plus size={16} />}>Nuovo servizio</Button>
        <Button variant="secondary" size="sm" icon={<FileText size={16} />} onClick={() => setShowExportDrawer(true)}>
          Esporta PDF
        </Button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={TOGGLE_STYLE.group}>
          {CATALOG_SCOPES.map(s => (
            <button key={s} style={TOGGLE_STYLE.btn(catalogScope === s)} onClick={() => setCatalogScope(s)}>
              {s === 'all' ? 'Tutti' : s === 'global' ? 'Globali' : 'Personali'}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={openCreate} disabled={catalogScope === 'global' && !canCatalogEdit}>
          <Plus size={14} /> Nuovo servizio
        </Button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{
          minWidth: 400, background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: gridCols,
            gap: '1rem', padding: '0.625rem 1rem',
            background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.10)',
            fontSize: 'var(--text-xs)', fontWeight: 600,
            color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
            alignItems: 'center',
          }}>
            <span>Servizio</span>
            <span>Descrizione</span>
            <span>Prezzo base</span>
            {showExtCol && <span>Prezzo esterno</span>}
            <span>Stato</span>
            <span />
          </div>

          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)
          ) : activeServices.length === 0 ? (
            <EmptyState title="Nessun servizio" description="Aggiungi servizi al listino prezzi" />
          ) : (
            activeServices.map(s => {
              const entry = overrideMap.get(s.id);
              const hasOverride = showExtCol && entry != null && entry.customPrice != null;
              const isGlobal = !s.ownerUserId && !s.ownerExternalPhotographerId;
              const canEditService = !isGlobal || canCatalogEdit;

              return (
                <div key={s.id} style={{
                  display: 'grid', gridTemplateColumns: gridCols,
                  gap: '1rem', padding: '0.75rem 1rem',
                  borderBottom: '1px solid rgba(255,255,255,0.08)', alignItems: 'center',
                }}>
                  <span style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{s.name}</span>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{s.description ?? '—'}</span>
                  <span style={{
                    fontSize: 'var(--text-sm)', fontWeight: 600,
                    textDecoration: hasOverride ? 'line-through' : 'none',
                    color: hasOverride ? 'var(--color-text-faint)' : 'var(--color-text)',
                  }}>
                    {formatCurrency(s.basePrice)}
                  </span>
                  {showExtCol && (
                    <span style={{ fontSize: 'var(--text-sm)', fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      {editingOverrideServiceId === s.id ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editingOverrideValue}
                          onChange={e => setEditingOverrideValue(e.target.value)}
                          onBlur={async () => {
                            const num = parseFloat(editingOverrideValue);
                            if (isNaN(num) || num < 0) { setEditingOverrideServiceId(null); return; }
                            setSavingOverride(true);
                            try {
                              if (num === s.basePrice || editingOverrideValue === '') {
                                if (entry) {
                                  await deleteOverride.mutateAsync({ extId: selectedExtId, serviceId: s.id });
                                  toast.success('Override rimosso');
                                }
                              } else {
                                await upsertOverride.mutateAsync({ extId: selectedExtId, serviceId: s.id, customPrice: num });
                                toast.success('Prezzo esterno aggiornato');
                              }
                            } catch (e) {
                              toast.error(e instanceof Error ? e.message : 'Errore');
                            } finally {
                              setSavingOverride(false);
                              setEditingOverrideServiceId(null);
                            }
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                            if (e.key === 'Escape') setEditingOverrideServiceId(null);
                          }}
                          autoFocus
                          disabled={savingOverride}
                          style={{
                            width: '90px', background: 'var(--glass-bg-card)', border: '1px solid var(--color-primary)',
                            borderRadius: 'var(--radius-interactive)', color: 'var(--color-text)', fontSize: 'var(--text-sm)',
                            padding: '0.125rem 0.375rem', outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                      ) : (
                        <span
                          onClick={() => {
                            setEditingOverrideServiceId(s.id);
                            setEditingOverrideValue(hasOverride ? String(entry!.customPrice!) : '');
                          }}
                          style={{ cursor: 'pointer', borderBottom: '1px dashed var(--color-text-faint)', paddingBottom: '1px' }}
                          title="Clicca per modificare"
                        >
                          {hasOverride ? (
                            <strong style={{ color: 'var(--color-primary)' }}>{formatCurrency(entry!.customPrice!)}</strong>
                          ) : (
                            <span style={{ color: 'var(--color-text-faint)' }}>—</span>
                          )}
                        </span>
                      )}
                      {hasOverride && editingOverrideServiceId !== s.id && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await deleteOverride.mutateAsync({ extId: selectedExtId, serviceId: s.id });
                              toast.success('Override rimosso');
                            } catch (e) {
                              toast.error(e instanceof Error ? e.message : 'Errore');
                            }
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error)', padding: '0.125rem', display: 'inline-flex', flexShrink: 0 }}
                          title="Rimuovi override"
                        >
                          <Trash2 size={11} />
                        </button>
                      )}
                    </span>
                  )}
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)' }}>Attivo</span>
                  {canEditService && (
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button onClick={() => openEdit(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)' }}>
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => setDeleteTarget(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)' }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editService ? 'Modifica servizio' : 'Nuovo servizio'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Annulla</Button>
            <Button onClick={handleSubmit} loading={createMutation.isPending || updateMutation.isPending}>
              {editService ? 'Salva' : 'Crea'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input data-testid="service-name" label="Nome *" value={name} onChange={e => setName(e.target.value)} required />
          <Input label="Descrizione" value={description} onChange={e => setDesc(e.target.value)} />
          <Input data-testid="service-basePrice" label="Prezzo base (€) *" type="number" value={basePrice} onChange={e => setBasePrice(e.target.value)} required min="0" step="0.01" />
          <Input
            label="Gruppo conflitto"
            value={conflictGroup}
            onChange={e => setConflictGroup(e.target.value)}
            placeholder="es. editing-base"
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Rimuovi servizio"
        description={`Il servizio "${deleteTarget?.name}" verrà disattivato.`}
        confirmLabel="Disattiva"
        loading={deleteMutation.isPending}
      />

      <ServiceExportPdfDrawer
        open={showExportDrawer}
        onClose={() => setShowExportDrawer(false)}
        services={services ?? []}
        externalPhotographerId={selectedExtId || undefined}
        externalPhotographerName={selectedExtName}
      />
    </div>
  );
}
